import { Modal } from '@pesto/ui/components/Modal';
import { CreditCheckContent } from '@pesto/ui/features/CreditApplication/components/CreditCheckContent';
import { OverviewDetails } from '@pesto/ui/features/CreditApplication/components/OverviewDetails';
import { CreditApplicationDetailScreen } from '@pesto/ui/features/CreditApplication/CreditApplicationDetailScreen';
import { useParams } from 'react-router-dom';

import { useGetCreditApplicationByIdQuery } from '../../__generated__/graphql/api';
import { UserDetails } from '../../components/UserDetails/UserDetails';
import { routes } from '../../constants/routes';
import { AdminActionRecordsContent } from './components/AdminActionRecordsContent';
import { DocCheckContent } from './components/DocCheckContent';
import { useApplicationData } from './hooks/useApplicationData';
import { ApplicationActions, useApplicationModal } from './hooks/useApplicationModal';
import { useCreditApplicationDetails } from './hooks/useCreditApplicationDetails';

export const CreditApplicationDetail = () => {
  const params = useParams();

  const { data, loading, refetch } = useGetCreditApplicationByIdQuery({
    variables: { creditApplicationId: params?.id ?? '' },
    fetchPolicy: 'cache-and-network',
  });

  const application = data?.creditApplication;

  const {
    setAction,
    showModal,
    actionLoading,
    setShowModal,
    modalContent,
    getModalTitle,
    handleModalConfirm,
    setDocCheckId,
    errors,
  } = useApplicationModal({
    application,
  });

  const errorMessage = errors?.find((e) => e)?.message;

  const handleButtonClick = (docCheckId: string) => {
    setDocCheckId(docCheckId);
    setAction(ApplicationActions.REVIEW_DOC_CHECK);
    setShowModal(true);
  };

  const { mainActionButtonsRender } = useCreditApplicationDetails();
  const { getApplicationData } = useApplicationData({ application, setShowModal, setAction });

  const collateralType = application?.pestoUser?.program?.collateralType;

  return (
    <div>
      <CreditApplicationDetailScreen
        breadcrumb={{
          items: [
            { href: routes.dashboard, label: 'Dashboard' },
            { href: routes.creditApplications, label: 'Credit Applications' },
            { label: `${application?.id}` },
          ],
        }}
        tabs={[
          {
            label: 'Application view',
            value: 'application',
            content: (
              <div className="flex flex-col gap-4">
                <OverviewDetails
                  actionButtons={mainActionButtonsRender({ status: application?.status, collateralType })}
                  applicationData={getApplicationData()}
                  onActionButtonClick={(action) => {
                    setAction(action);
                    setShowModal(true);
                  }}
                />
              </div>
            ),
          },
          { label: 'User view', value: 'user', content: <UserDetails userId={application?.pestoUser?.id} /> },
          {
            label: 'Admin Action Records',
            value: 'adminActionRecords',
            amount: application?.adminActionRecords?.length,
            content: <AdminActionRecordsContent application={application} />,
          },
          {
            label: 'Credit Check',
            value: 'creditCheck',
            amount: application?.creditChecks?.length,
            content: <CreditCheckContent title="Credit Checks" checks={application?.creditChecks} />,
          },
          {
            label: 'Doc Check',
            value: 'docCheck',
            amount: application?.docChecks?.length,
            content: (
              <DocCheckContent checks={application?.docChecks} title="Doc Checks" onButtonClick={handleButtonClick} />
            ),
          },
        ]}
        loading={loading}
      />

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={getModalTitle()}
        onConfirm={() => void handleModalConfirm(refetch)}
        loading={actionLoading}
      >
        <div>
          {errorMessage && (
            <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">{errorMessage}</div>
          )}
          {modalContent()}
        </div>
      </Modal>
    </div>
  );
};
